import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import { inr } from "@/lib/format";
import { Wallet, Download, Plus, X, Pencil, Trash2, Lock } from "lucide-react";

const METHODS = [
  { v: "bank_transfer", label: "Bank Transfer" },
  { v: "cash", label: "Cash" },
  { v: "cheque", label: "Cheque" },
  { v: "upi", label: "UPI" },
  { v: "other", label: "Other" },
];
const STATUSES = ["pending", "partial", "paid"];

const STATUS_STYLES = {
  paid: "bg-[#1F7A3D]/10 text-[#1F7A3D] border-[#1F7A3D]/30",
  partial: "bg-[#C89B3C]/10 text-[#A0741F] border-[#C89B3C]/40",
  pending: "bg-[#5C6B7A]/10 text-[#5C6B7A] border-[#5C6B7A]/30",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso?.slice(0, 10) || "—"; }
};

function StatCard({ icon: Icon, label, value, accent = "navy", testid }) {
  const accentMap = {
    navy:   "from-[#0F2540] to-[#1E4F87]",
    green:  "from-[#1F7A3D] to-[#2BA85B]",
    maroon: "from-[#A53A2A] to-[#D85240]",
  };
  return (
    <div className={`relative overflow-hidden rounded-3xl p-5 text-white bg-gradient-to-br ${accentMap[accent]}`} data-testid={testid}>
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/15 blur-xl" />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] opacity-85">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold">{inr(value || 0)}</div>
        </div>
        <Icon className="h-6 w-6 opacity-80" />
      </div>
    </div>
  );
}

export default function AdminPayouts() {
  const [summary, setSummary] = useState({ employees: [], totals: { earned: 0, paid: 0, pending: 0 } });
  const [search, setSearch] = useState("");
  const [openEmp, setOpenEmp] = useState(null);            // employee row currently open
  const [history, setHistory] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [editing, setEditing] = useState(null);            // payout being edited (or "new")
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const load = async () => {
    try {
      const r = await api.get("/payouts/summary");
      setSummary(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load summary"); }
  };
  const loadHistory = async (employee_id, page = 1, limit = 20) => {
    try {
      const r = await api.get(`/payouts?employee_id=${employee_id}&page=${page}&limit=${limit}`);
      setHistory(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load history"); }
  };
  useEffect(() => { load(); }, []);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summary.employees;
    return summary.employees.filter((e) =>
      [e.name, e.referral_code, e.mobile, e.department].filter(Boolean).some((v) => v.toLowerCase().includes(q)),
    );
  }, [summary.employees, search]);

  // Client-side pagination of the filtered employee list.
  const pagedEmployees = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredEmployees.slice(start, start + limit);
  }, [filteredEmployees, page, limit]);

  // Whenever the search/limit changes, jump back to page 1 so we don't sit on an empty page.
  useEffect(() => { setPage(1); }, [search, limit]);

  const exportCsv = () => {
    const rows = [["Name", "Code", "Mobile", "Department", "Bank", "Account No", "IFSC", "PAN", "Earned", "Paid", "Pending", "Last Paid"]];
    filteredEmployees.forEach((e) => {
      rows.push([e.name || "", e.referral_code || "", e.mobile || "", e.department || "",
        e.bank_name || "", e.account_no || "", e.ifsc || "", e.pan || "",
        e.total_earned, e.total_paid, e.pending_balance, fmtDate(e.last_paid_at)]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `npc-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const openHistory = (emp) => {
    setOpenEmp(emp);
    loadHistory(emp.employee_id);
  };
  const startNew = () => {
    setEditing({
      employee_id: openEmp.employee_id,
      amount: openEmp.pending_balance > 0 ? Math.round(openEmp.pending_balance) : "",
      period: new Date().toISOString().slice(0, 7),
      method: "bank_transfer",
      reference: "", notes: "",
      status: "paid",
      paid_at: new Date().toISOString().slice(0, 10),
      _new: true,
    });
  };
  const startEdit = (p) => {
    if (p.locked) return toast.error("This payout is locked");
    setEditing({ ...p, paid_at: (p.paid_at || "").slice(0, 10) });
  };
  const removePayout = async (p) => {
    if (p.locked) return toast.error("This payout is locked");
    if (!window.confirm(`Delete this payout of ${inr(p.amount)}?`)) return;
    try {
      await api.delete(`/payouts/${p.payout_id}`);
      toast.success("Deleted");
      await loadHistory(openEmp.employee_id, history.page, history.limit);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async () => {
    const e = editing;
    if (!e.amount || Number(e.amount) <= 0) return toast.error("Amount must be > 0");
    if (!e.method) return toast.error("Method is required");
    setBusy(true);
    try {
      const body = {
        employee_id: e.employee_id,
        amount: Number(e.amount),
        period: e.period || null,
        method: e.method,
        reference: e.reference || "",
        notes: e.notes || "",
        status: e.status,
        paid_at: e.paid_at ? new Date(e.paid_at).toISOString() : null,
      };
      if (e._new) await api.post("/payouts", body);
      else await api.patch(`/payouts/${e.payout_id}`, body);
      toast.success("Saved");
      setEditing(null);
      await loadHistory(openEmp.employee_id, history.page, history.limit);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-payouts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl font-bold text-[#0F2540]">Employee Payouts</h2>
          <p className="text-sm text-[#5C6B7A] mt-1">Track referral incentives earned, paid out and the balance still owed.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="btn-ghost cursor-pointer" data-testid="payouts-bulk-import-label">
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={async (ev) => {
                const f = ev.target.files?.[0];
                ev.target.value = "";
                if (!f) return;
                const text = await f.text();
                const lines = text.trim().split(/\r?\n/);
                const startsWithHeader = /employee_code/i.test(lines[0]);
                const dataLines = startsWithHeader ? lines.slice(1) : lines;
                const rows = dataLines.map((l) => {
                  const [employee_code, amount, period, method, reference, notes, status, paid_at] =
                    l.split(",").map((x) => (x || "").trim());
                  return { employee_code, amount: Number(amount || 0), period, method, reference, notes, status, paid_at };
                }).filter((r) => r.employee_code && r.amount > 0 && r.period);
                if (!rows.length) { toast.error("No valid rows found in CSV"); return; }
                try {
                  const r = await api.post("/payouts/bulk-import", { rows });
                  const skipped = r.data.skipped || [];
                  if (skipped.length) toast.error(`Imported ${r.data.created} · skipped ${skipped.length}`);
                  else toast.success(`Imported ${r.data.created} payouts`);
                  await load();
                } catch (e) { toast.error(e.response?.data?.detail || "Bulk import failed"); }
              }}
            />
            <span data-testid="payouts-bulk-import-btn">+ Bulk Import (CSV)</span>
          </label>
          <button onClick={exportCsv} className="btn-ghost" data-testid="payouts-export-btn">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard icon={Wallet} label="Total earned" value={summary.totals.earned} accent="navy" testid="payouts-stat-earned" />
        <StatCard icon={Wallet} label="Total paid" value={summary.totals.paid} accent="green" testid="payouts-stat-paid" />
        <StatCard
          icon={Wallet}
          label={summary.totals.pending < 0 ? "Over-paid" : "Pending balance"}
          value={Math.abs(summary.totals.pending)}
          accent={summary.totals.pending < 0 ? "navy" : "maroon"}
          testid="payouts-stat-pending"
        />
      </div>

      {/* Search + table */}
      <div className="rounded-3xl border border-[#E1E8F0] bg-white overflow-hidden">
        <div className="p-4 border-b border-[#E1E8F0]">
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name / code / mobile / department"
            className="w-full rounded-xl border border-[#E1E8F0] px-4 py-2.5 text-sm focus:border-[#1E4F87] outline-none"
            data-testid="payouts-search"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F7FB] text-[#5C6B7A] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Employee</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-right px-4 py-3">Earned</th>
                <th className="text-right px-4 py-3">Paid</th>
                <th className="text-right px-4 py-3">Pending</th>
                <th className="text-left px-4 py-3">Last paid</th>
                <th className="text-right px-4 py-3 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedEmployees.map((e) => (
                <tr key={e.employee_id} className="border-t border-[#E1E8F0] hover:bg-[#F4F7FB]/40" data-testid={`payout-row-${e.referral_code || e.employee_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0F2540]">{e.name}</div>
                    <div className="text-xs text-[#5C6B7A]">{e.department || "—"} · {e.mobile || "—"}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{e.referral_code || "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold">{inr(e.total_earned)}</td>
                  <td className="px-4 py-3 text-right text-[#1F7A3D]">{inr(e.total_paid)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${e.pending_balance > 0 ? "text-[#A53A2A]" : "text-[#5C6B7A]"}`}>{inr(e.pending_balance)}</td>
                  <td className="px-4 py-3 text-[#5C6B7A]">{fmtDate(e.last_paid_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openHistory(e)} className="text-xs text-[#1E4F87] underline" data-testid={`payout-open-${e.referral_code || e.employee_id}`}>Manage →</button>
                  </td>
                </tr>
              ))}
              {filteredEmployees.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-[#5C6B7A]" data-testid="payouts-empty">No employees match this search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {filteredEmployees.length > 0 && (
          <div className="p-3 border-t border-[#E1E8F0]">
            <Pagination
              page={page} limit={limit} total={filteredEmployees.length}
              onPageChange={(p) => setPage(p)}
              onLimitChange={(l) => setLimit(l)}
              testid="payouts-pagination"
            />
          </div>
        )}
      </div>

      {/* History modal */}
      {openEmp && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => !busy && setOpenEmp(null)}>
          <div onClick={(ev) => ev.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col" data-testid="payouts-history-modal">
            <div className="px-6 pt-5 pb-4 border-b border-[#E1E8F0] flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-xl font-bold text-[#0F2540]">{openEmp.name}</h3>
                <p className="text-xs text-[#5C6B7A] mt-1">Code <span className="font-mono">{openEmp.referral_code}</span> · Earned {inr(openEmp.total_earned)} · Paid {inr(openEmp.total_paid)} · <b className="text-[#A53A2A]">Pending {inr(openEmp.pending_balance)}</b></p>
              </div>
              <button onClick={() => !busy && setOpenEmp(null)} className="text-[#5C6B7A] hover:text-[#0F2540] text-xl leading-none px-2" data-testid="payouts-history-close">×</button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[#0F2540]">Payout history</h4>
                <button onClick={startNew} className="btn-primary !py-1.5 !text-xs" data-testid="payouts-add-btn">
                  <Plus className="h-3.5 w-3.5" /> Add payout
                </button>
              </div>
              {history.items.length === 0 && (
                <div className="rounded-xl border-2 border-dashed border-[#E1E8F0] p-6 text-center text-sm text-[#5C6B7A]">No payouts yet for this employee.</div>
              )}
              <div className="space-y-2">
                {history.items.map((p) => (
                  <div key={p.payout_id} className="rounded-xl border border-[#E1E8F0] p-3 flex items-start gap-3 hover:border-[#C8D6E5] transition" data-testid={`payout-item-${p.payout_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-bold text-[#0F2540]">{inr(p.amount)}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${STATUS_STYLES[p.status] || STATUS_STYLES.pending}`}>{p.status}</span>
                        {p.locked && <span className="text-[10px] text-[#5C6B7A] inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Locked</span>}
                        <span className="text-xs text-[#5C6B7A]">{p.period || "all"}</span>
                      </div>
                      <div className="text-xs text-[#5C6B7A] mt-1">
                        {METHODS.find((m) => m.v === p.method)?.label || p.method}
                        {p.reference ? <> · Ref <span className="font-mono">{p.reference}</span></> : null}
                        {" · "}Paid {fmtDate(p.paid_at)}
                      </div>
                      {p.notes && <p className="text-xs text-[#0F2540] mt-1 italic">"{p.notes}"</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => startEdit(p)} disabled={p.locked} className="p-1.5 rounded-lg hover:bg-[#EAF1FA] text-[#1E4F87] disabled:opacity-30" data-testid={`payout-edit-${p.payout_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => removePayout(p)} disabled={p.locked} className="p-1.5 rounded-lg hover:bg-[#E63946]/10 text-[#E63946] disabled:opacity-30" data-testid={`payout-del-${p.payout_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Pagination
                  page={history.page} limit={history.limit} total={history.total}
                  onPageChange={(p) => loadHistory(openEmp.employee_id, p, history.limit)}
                  onLimitChange={(l) => loadHistory(openEmp.employee_id, 1, l)}
                  testid="payouts-history-pagination"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit form */}
      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={() => !busy && setEditing(null)}>
          <div onClick={(ev) => ev.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-6" data-testid="payouts-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-[#0F2540]">{editing._new ? "Record a new payout" : "Edit payout"}</h3>
              <button onClick={() => !busy && setEditing(null)} className="text-[#5C6B7A] hover:text-[#0F2540] text-xl px-2"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Amount (₹) *" type="number" v={editing.amount} s={(v) => setEditing({ ...editing, amount: v })} testid="payout-form-amount" />
              <Field label="Period (YYYY-MM)" type="month" v={editing.period} s={(v) => setEditing({ ...editing, period: v })} testid="payout-form-period" />
              <div>
                <label className="text-xs font-medium text-[#5C6B7A] block mb-1">Method *</label>
                <select value={editing.method} onChange={(ev) => setEditing({ ...editing, method: ev.target.value })}
                  className="w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm" data-testid="payout-form-method">
                  {METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C6B7A] block mb-1">Status *</label>
                <select value={editing.status} onChange={(ev) => setEditing({ ...editing, status: ev.target.value })}
                  className="w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm" data-testid="payout-form-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                </select>
              </div>
              <Field label="Reference / Txn #" v={editing.reference} s={(v) => setEditing({ ...editing, reference: v })} testid="payout-form-ref" />
              <Field label="Paid on" type="date" v={editing.paid_at} s={(v) => setEditing({ ...editing, paid_at: v })} testid="payout-form-date" />
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-[#5C6B7A] block mb-1">Notes / remarks</label>
                <textarea value={editing.notes} onChange={(ev) => setEditing({ ...editing, notes: ev.target.value })} rows={2}
                  className="w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2 text-sm" data-testid="payout-form-notes" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => !busy && setEditing(null)} disabled={busy}>Cancel</button>
              <button className="btn-primary" onClick={submit} disabled={busy} data-testid="payout-form-submit">{busy ? "Saving…" : "Save payout"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, s, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#5C6B7A] block mb-1">{label}</label>
      <input value={v ?? ""} onChange={(e) => s(e.target.value)} type={type}
        className="w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 text-sm focus:border-[#1E4F87] outline-none"
        data-testid={testid} />
    </div>
  );
}
